import { CellValueChangedEvent, StatusPanelDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo } from 'react';

import { useElementActions, useGetElements } from 'bom/hooks';
import { IElementListRequestPayload } from 'bom/types';
import { AgGridTable } from 'core/components';
import { NotificationPosition, SortDirection, TableLayoutType } from 'core/enums';
import { AgGridClientTableProvider } from 'core/states';
import { AgGridDataRow } from 'core/types';
import { AddLineButton, ImportBomButton, ViewByButton } from 'jobs/components/JobDetails';
import { IJob } from 'jobs/types';

import { columnDef } from './columns';
import { DetailElement } from './DetailElement';
import { SaveAsTemplateButton } from './selectionActions';
import { TotalsStatusBar } from './TotalsStatusBar';

interface IProps {
  job: IJob;
}

export const ElementList = ({ job }: IProps) => {
  const { upsertElement } = useElementActions({ notificationPosition: NotificationPosition.TOP });

  const columnDefs = useMemo(() => columnDef(job), [job]);

  const defaultColDef = useMemo(() => ({ autoHeight: true, enableRowGroup: false }), []);

  const detailCellRendererParams = useMemo(() => ({ job }), [job]);

  const payload: IElementListRequestPayload = useMemo(
    () => ({
      filters: { bomId: job.id },
      sortField: ['line'],
      sortDir: [SortDirection.ASC],
      includeCost: 1,
    }),
    [job.id]
  );

  const {
    records,
    isLoading: isDataLoading,
    loadingPercentage,
    currentPage,
  } = useGetElements(payload, { fetchAllPages: true });

  const isDataLoadCompleted = useMemo(() => loadingPercentage === 100, [loadingPercentage]);

  const statusBar = useMemo<{
    statusPanels: StatusPanelDef[];
  }>(
    () => ({
      statusPanels: [
        {
          statusPanel: TotalsStatusBar,
          align: 'left',
        },
      ],
    }),
    []
  );

  const actionButtons = useCallback(
    () => [
      <AddLineButton key="addElementButton" jobId={job.id} />,
      <ImportBomButton key="importBomButton" job={job} />,
    ],
    [job]
  );

  const rightActionButtons = useMemo(() => [<ViewByButton key="viewByButton" />], []);

  const selectionActionButtons = useMemo(
    () => [<SaveAsTemplateButton key="saveAsTemplateButton" />],
    []
  );

  const onCellValueChanged = useCallback(
    async (
      _: AgGridReact,
      event: CellValueChangedEvent
    ): Promise<{ updatedRow?: AgGridDataRow }> => {
      const {
        success,
        element: updatedElement,
        error,
      } = await upsertElement({
        element: {
          id: event.data.id,
          [event.colDef.field]: event.newValue,
        },
        includeCost: 1,
      });

      if (!success) {
        throw new Error(error);
      }

      return { updatedRow: updatedElement };
    },
    [upsertElement]
  );

  return (
    <AgGridClientTableProvider
      tableId="jobElementListTable"
      entityType="Element"
      layoutType={TableLayoutType.JOB_ELEMENT}
      defaultColDef={defaultColDef}
      columnDefs={columnDefs}
      data={records}
      isDataLoading={isDataLoading}
      loadingPercentage={loadingPercentage}
      dataCurrentPage={currentPage}
      onCellValueChanged={onCellValueChanged}
      headerCheckbox={isDataLoadCompleted}
      hasLayoutActions
      masterDetail
      detailRowAutoHeight
      detailCellRenderer={DetailElement}
      detailCellRendererParams={detailCellRendererParams}
      isRowMaster={() => true}
      statusBar={statusBar}
    >
      <AgGridTable
        actionButtons={actionButtons}
        rightActionButtons={rightActionButtons}
        selectionActionButtons={selectionActionButtons}
      />
    </AgGridClientTableProvider>
  );
};
